// Pure helpers for the "Estimating Turnaround" widget.
//
// The widget renders a per-salesperson matrix with three period groups
// (Week / Quarter / Year to Date). Each period carries five metrics; see
// PERIOD_COLUMNS below. `outsideCubicasa` is the count of floorplans built
// WITHOUT the CubiCasa API app (so the business can spot people skirting that
// system). Kept as a plain count for now; it can become a drill-down link
// later without changing this contract.

// The period groups, in display order.
export const PERIODS = [
  { key: "week", label: "Week to Date" },
  { key: "qtd", label: "Quarter to Date" },
  { key: "ytd", label: "Year to Date" },
];

// The five metric columns within each period group, in display order.
export const PERIOD_COLUMNS = [
  { key: "requested", label: "Estimates Requested" },
  { key: "completed", label: "Estimates Complete" },
  { key: "pctComplete", label: "Percent Complete", suffix: "%" },
  { key: "avgTurnaroundDays", label: "Avg Turn Around (Days)" },
  { key: "outsideCubicasa", label: "Imported Outside CubiCasa" },
];

export const EMPTY_PERIOD = {
  requested: 0,
  completed: 0,
  pctComplete: 0,
  avgTurnaroundDays: 0,
  outsideCubicasa: 0,
};

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const normalizePeriod = (p) => {
  if (!p || typeof p !== "object") return { ...EMPTY_PERIOD };
  return {
    requested: num(p.requested),
    completed: num(p.completed),
    pctComplete: num(p.pctComplete),
    avgTurnaroundDays: num(p.avgTurnaroundDays),
    outsideCubicasa: num(p.outsideCubicasa),
  };
};

// A row is recognised only if it names a salesperson — this distinguishes the
// new per-salesperson array from the legacy aggregate object.
const isSalespersonRow = (r) =>
  r && typeof r === "object" && ("salespersonName" in r || "salespersonId" in r);

// Map whatever the API returns into Row[]. Returns [] for anything that isn't
// the new per-salesperson array (including the legacy aggregate shape), which
// signals the widget to fall back to mock data until the backend is updated.
export function normalizeEstimatingTurnaround(data) {
  const arr = Array.isArray(data) ? data : Array.isArray(data?.result) ? data.result : null;
  if (!arr || !arr.some(isSalespersonRow)) return [];
  return arr.filter(isSalespersonRow).map((r) => ({
    salespersonId: r.salespersonId ?? null,
    salespersonName: r.salespersonName ?? "",
    week: normalizePeriod(r.week),
    qtd: normalizePeriod(r.qtd),
    ytd: normalizePeriod(r.ytd),
  }));
}

const round1 = (n) => Math.round(n * 10) / 10;

const sumPeriod = (rows, key) =>
  rows.reduce(
    (acc, row) => {
      const p = row[key] || EMPTY_PERIOD;
      acc.requested += p.requested;
      acc.completed += p.completed;
      acc.outsideCubicasa += p.outsideCubicasa;
      acc.turnaroundWeighted += p.avgTurnaroundDays * p.completed;
      return acc;
    },
    { requested: 0, completed: 0, outsideCubicasa: 0, turnaroundWeighted: 0 }
  );

// Build the bottom "Totals" row that aggregates all salespeople. Percent is
// recomputed from summed totals (not an average of percents) and average
// turnaround is weighted by completed count.
//
// Percent Complete is clamped to 100%: within a short window "Estimates
// Complete" can exceed "Estimates Requested" (completions counted this period
// were often requested in an earlier one), so the raw ratio can run over 100%.
// Capping keeps the Totals cell reading as a sensible completion percentage.
export function computeTotalsRow(rows) {
  const forPeriod = (key) => {
    const s = sumPeriod(rows, key);
    return {
      requested: s.requested,
      completed: s.completed,
      outsideCubicasa: s.outsideCubicasa,
      pctComplete: s.requested
        ? Math.min(100, Math.round((s.completed / s.requested) * 100))
        : 0,
      avgTurnaroundDays: s.completed ? round1(s.turnaroundWeighted / s.completed) : 0,
    };
  };
  return {
    salespersonId: null,
    salespersonName: "Totals",
    week: forPeriod("week"),
    qtd: forPeriod("qtd"),
    ytd: forPeriod("ytd"),
  };
}
